import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView } from 'framer-motion';
import confetti from 'canvas-confetti';
import { professionalCertification } from './constants';

export default function Certification() {
  const [showCongrats, setShowCongrats] = useState(false);
  const [latestCert, setLatestCert] = useState(null);
  const certSectionRef = useRef(null);
  const isInView = useInView(certSectionRef, { once: true });

  useEffect(() => {
    // Check if confetti already shown
    const confettiShown = localStorage.getItem('confettiShown');

    // Check for recent cert (last 30 days)
    const oneMonthAgo = new Date();
    oneMonthAgo.setDate(oneMonthAgo.getDate() - 30);
    const recentCert = professionalCertification.find((cert) => {
      const certDate = new Date(cert.date);
      return certDate >= oneMonthAgo;
    });

    // Trigger confetti only once when section in view & recent cert exists & not shown before
    // if (isInView && recentCert && !confettiShown) {
    if (isInView && recentCert) {
      setLatestCert(recentCert);
      setShowCongrats(true);
      // localStorage.setItem("confettiShown", "true");

      const duration = 2 * 1000;
      const end = Date.now() + duration;

      (function frame() {
        confetti({
          particleCount: 5,
          angle: 60,
          spread: 55,
          origin: { x: 0 },
          colors: ['#f59e0b', '#ec4899', '#6366f1', '#22d3ee'],
        });
        confetti({
          particleCount: 5,
          angle: 120,
          spread: 55,
          origin: { x: 1 },
          colors: ['#f59e0b', '#ec4899', '#6366f1', '#22d3ee'],
        });

        if (Date.now() < end) {
          requestAnimationFrame(frame);
        }
      })();

      // Hide banner after 5 seconds
      setTimeout(() => setShowCongrats(false), 5000);
    }
  }, [isInView]);

  return (
    <section
      className="pt-20 pb-10 px-4 relative"
      id="acheivements"
      ref={certSectionRef}
    >
      {/* Congratulatory banner */}
      {showCongrats && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          className="fixed top-20 left-1/2 transform -translate-x-1/2 bg-primary text-white px-6 py-3 rounded-xl shadow-lg z-50"
        >
          🎉 I'm thrilled to announce I recently earned the <br />{' '}
          <strong>{latestCert?.title}</strong> certification!
        </motion.div>
      )}

      <div className="max-w-7xl mx-auto">
        <h2 className="text-4xl font-bold mb-10 text-center text-black dark:text-white">
          Licenses & Certifications
        </h2>

        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {professionalCertification.map((cert, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: index * 0.1 }}
              viewport={{ once: true }}
              className="group relative p-6 rounded-2xl border border-primary/30 dark:border-primary/50 shadow-md hover:shadow-xl bg-white/80 dark:bg-neutral-900/80 backdrop-blur-sm transition-all"
            >
              {/* Tooltip */}
              <div className="absolute top-2 right-2 text-xs text-white bg-primary px-2 py-0.5 rounded-md opacity-0 group-hover:opacity-100 transition">
                {cert.title}
              </div>

              {/* Image & Title */}
              <div className="flex flex-col items-center gap-4 mb-4">
                <img
                  src={cert.img}
                  alt={cert.issuer}
                  className="w-34 sm:w-28 object-contain"
                />
                <h3 className="text-lg font-semibold text-center text-black dark:text-white">
                  {cert.title}
                </h3>
                <p className="text-sm text-gray-600 dark:text-gray-400 text-center">
                  {cert.issuer}
                </p>
              </div>

              {/* Issue Date */}
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
                Issue Date: {cert.issuedDate}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400 text-center mb-4">
                Expiry Date: {cert.expiryDate}
              </p>

              {/* CTA Button */}
              <div className="flex justify-center">
                <a
                  href={cert.link}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-block bg-primary text-white text-sm px-4 py-2 rounded-lg hover:bg-primary-dark transition"
                >
                  View Credential
                </a>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
